import { Component } from '@theme/component';

/** How long to wait for Shopify to answer before pointing the visitor at the email address. */
const RESPONSE_TIMEOUT = 20000;

/**
 * Keeps the landing page in place while feedback is submitted.
 *
 * The form posts into a hidden iframe so the visitor stays on the page and gets an
 * inline confirmation instead of a full page load.
 *
 * The dialog markup lives inside `dialog-component`, so its elements belong to that
 * component and are queried here rather than taken through `ref`.
 *
 * @extends Component
 */
export class FeedbackFormComponent extends Component {
  requiredRefs = ['frame'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** Whether a submission is waiting for the iframe response. */
  #pending = false;

  /** @type {ReturnType<typeof setTimeout> | number} */
  #timeoutId = 0;

  connectedCallback() {
    super.connectedCallback();

    const options = { signal: this.#abortController.signal };

    this.refs.frame.addEventListener('load', this.#handleFrameLoad, options);
    this.#form?.addEventListener('submit', this.#handleSubmit, options);
    this.#againButton?.addEventListener('click', this.#handleReset, options);
    this.#dialogHost?.addEventListener('dialog:close', this.#handleDialogClose, options);

    // Only post into the frame when the browser can handle the interaction.
    if (this.#form) {
      this.#form.target = this.refs.frame.name;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    clearTimeout(this.#timeoutId);
  }

  get #panel() {
    return this.querySelector('.feedback__panel');
  }

  get #form() {
    return this.#panel?.querySelector('.contact-form__form');
  }

  get #submitButton() {
    return this.#form?.querySelector('button[type="submit"]');
  }

  get #sending() {
    return this.querySelector('.feedback__sending');
  }

  get #success() {
    return this.querySelector('.feedback__message--success');
  }

  get #error() {
    return this.querySelector('.feedback__message--error');
  }

  get #againButton() {
    return this.#success?.querySelector('.feedback__again');
  }

  get #dialogHost() {
    return this.querySelector('dialog-component');
  }

  /**
   * Marks the form as pending while it posts into the iframe.
   *
   * The submission is deliberately not prevented: it has to be a real browser POST,
   * because Shopify rejects programmatic requests to the contact endpoint.
   */
  #handleSubmit = () => {
    this.#pending = true;
    this.#showStatus(this.#sending);

    if (this.#submitButton) {
      this.#submitButton.disabled = true;
      this.#submitButton.setAttribute('aria-busy', 'true');
    }

    clearTimeout(this.#timeoutId);
    this.#timeoutId = setTimeout(this.#handleTimeout, RESPONSE_TIMEOUT);
  };

  /**
   * Restores the form after a confirmation so another message can be sent.
   */
  #handleReset = () => {
    this.#form?.reset();
    this.#showStatus(null);

    if (this.refs.frame instanceof HTMLIFrameElement) {
      this.refs.frame.src = 'about:blank';
    }

    this.#form?.querySelector('input:not([type="hidden"])')?.focus();
  };

  #handleFrameLoad = () => {
    if (!this.#pending) return;

    this.#pending = false;
    clearTimeout(this.#timeoutId);

    // Shopify re-renders the form with field errors when it rejects the submission.
    const rejected = Boolean(this.refs.frame.contentDocument?.querySelector('.contact-form__error'));

    this.#showStatus(rejected ? this.#error : this.#success);
  };

  #handleTimeout = () => {
    if (!this.#pending) return;

    this.#pending = false;
    this.#showStatus(this.#error);
  };

  #handleDialogClose = () => {
    if (this.#pending) return;

    const messageShowing = [this.#success, this.#error].some((element) => element && !element.hidden);
    if (messageShowing) {
      this.#handleReset();
    }
  };

  #showStatus(status) {
    for (const element of [this.#sending, this.#success, this.#error]) {
      if (element) element.hidden = element !== status;
    }

    const panel = this.#panel;
    if (panel) {
      panel.hidden = Boolean(status) && status !== this.#sending;
    }

    if (this.#submitButton) {
      this.#submitButton.disabled = false;
      this.#submitButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('feedback-form')) {
  customElements.define('feedback-form', FeedbackFormComponent);
}